import React, { useState } from 'react'
import styled from 'styled-components'
import { useTheme } from '../../../middleware/ThemeContext';
import { font, Theme } from '../../../utils/themes';
import StyledText from '../../StyledComponents/StyledText';
import { TempSettings, CanvasItem, Element } from '../../../types/types';
import type { Section as SectionType } from '../../../types/types';
import { hexToRgba } from '../../../utils/helper';
import { StyledHorizontalLine } from '../../StyledComponents/Styledhr';
import ImageIcon from '../../../assets/icons/ImageIcon';
import VideoIcon from '../../../assets/icons/VideoIcon';
import { Formik } from 'formik';
import FormikImageUpload from '../../Formik/FormikImageUpload';
import { useToast } from '../../toast/ToastContext';
import { useLogoPreSignedUrlMutation } from '../../../api/pageBuilderApiSlice';
import { Form, Row } from 'react-bootstrap';
import DraggableIcon from '../../../assets/icons/DraggableIcon';
import DeleteIcon from '../../../assets/icons/DeleteIcon';
import { useEffect } from 'react';

interface UploadedFile {
    file: File;
    name: string;
    type: string;
    rename: string;
    progress: number;
}

interface SectionPropertiesProps {
    updateTempSetting: (key: string, value: any) => void;
    tempSettings: TempSettings;
    sectionId?: string;
    canvasItems?: CanvasItem[];
    setCanvasItems?: React.Dispatch<React.SetStateAction<CanvasItem[]>>;
    onUploadStateChange?: (isUploading: boolean) => void;
}

const SectionProperties = ({ updateTempSetting, tempSettings, sectionId, canvasItems, setCanvasItems, onUploadStateChange }: SectionPropertiesProps) => {
    const { theme } = useTheme();
    const showToast = useToast();
    const [mediaPreSignedUrl] = useLogoPreSignedUrlMutation();
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadKey, setUploadKey] = useState(0);
    const [hasUploaded, setHasUploaded] = useState<boolean>(false);

    // Drag and drop states
    const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
    const [dragOverItemId, setDragOverItemId] = useState<string | null>(null);
    const [draggedColumn, setDraggedColumn] = useState<'left' | 'right' | undefined>(undefined);

    useEffect(() => {
        onUploadStateChange?.(isUploading);
    }, [isUploading, onUploadStateChange]);

    const selectedMediaType = tempSettings.mediaType || 'Image';

    // Get current section
    const currentSection = canvasItems?.find(item => item.id === sectionId) as SectionType | undefined;
    const isSingleColumn = currentSection?.type === 'single_column';

    // Get elements based on section type
    const getSectionElements = (): { elements: Element[], column?: 'left' | 'right' }[] => {
        if (!currentSection) return [];

        if (isSingleColumn) {
            return [{ elements: currentSection.elements || [] }];
        } else {
            return [
                { elements: currentSection.columns?.left || [], column: 'left' as const },
                { elements: currentSection.columns?.right || [], column: 'right' as const }
            ];
        }
    };

    // Drag and drop handlers
    const handleDragStart = (elementId: string, column?: 'left' | 'right') => {
        setDraggedItemId(elementId);
        setDraggedColumn(column);
    };

    const handleDragOver = (e: React.DragEvent, elementId: string) => {
        e.preventDefault();
        e.stopPropagation();
        if (draggedItemId !== elementId) {
            setDragOverItemId(elementId);
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverItemId(null);
    };

    const handleDrop = (e: React.DragEvent, targetElementId: string, targetColumn?: 'left' | 'right') => {
        e.preventDefault();
        e.stopPropagation();

        if (!setCanvasItems || !currentSection || !draggedItemId) return;

        // Don't do anything if dropping on itself
        if (draggedItemId === targetElementId) {
            setDraggedItemId(null);
            setDragOverItemId(null);
            setDraggedColumn(undefined);
            return;
        }

        // Don't allow cross-column dragging in multi-column sections
        if (!isSingleColumn && draggedColumn !== targetColumn) {
            setDraggedItemId(null);
            setDragOverItemId(null);
            setDraggedColumn(undefined);
            return;
        }

        setCanvasItems(prevItems => {
            return prevItems.map(item => {
                if (item.id !== sectionId) return item;

                const section = item as SectionType;

                if (isSingleColumn) {
                    const elements = [...(section.elements || [])];
                    const draggedIndex = elements.findIndex(el => el.id === draggedItemId);
                    const targetIndex = elements.findIndex(el => el.id === targetElementId);

                    if (draggedIndex === -1 || targetIndex === -1) return item;

                    // Remove dragged item and insert at target position
                    const [draggedItem] = elements.splice(draggedIndex, 1);
                    elements.splice(targetIndex, 0, draggedItem);

                    return { ...section, elements };
                } else {
                    if (!targetColumn) return item;

                    const columns = {
                        left: section.columns?.left || [],
                        right: section.columns?.right || []
                    };
                    const elements = [...(columns[targetColumn] || [])];
                    const draggedIndex = elements.findIndex(el => el.id === draggedItemId);
                    const targetIndex = elements.findIndex(el => el.id === targetElementId);

                    if (draggedIndex === -1 || targetIndex === -1) return item;

                    // Remove dragged item and insert at target position
                    const [draggedItem] = elements.splice(draggedIndex, 1);
                    elements.splice(targetIndex, 0, draggedItem);
                    columns[targetColumn] = elements;

                    return { ...section, columns };
                }
            });
        });

        setDraggedItemId(null);
        setDragOverItemId(null);
        setDraggedColumn(undefined);
    };

    const handleDragEnd = () => {
        setDraggedItemId(null);
        setDragOverItemId(null);
        setDraggedColumn(undefined);
    };

    // Delete element handler
    const handleDeleteElement = (elementId: string, column?: 'left' | 'right') => {
        if (!setCanvasItems || !currentSection) return;

        setCanvasItems(prevItems => {
            return prevItems.map(item => {
                if (item.id !== sectionId) return item;

                const section = item as SectionType;

                if (isSingleColumn) {
                    const elements = (section.elements || []).filter(el => el.id !== elementId);
                    return { ...section, elements };
                } else {
                    if (!column) return item;

                    const columns = {
                        left: section.columns?.left || [],
                        right: section.columns?.right || []
                    };
                    columns[column] = columns[column].filter(el => el.id !== elementId);

                    return { ...section, columns };
                }
            });
        });
    };

    // Get media data based on current media type
    const getMediaData = (field: string) => {
        const mediaTypePrefix = selectedMediaType.toLowerCase();
        return tempSettings[`${mediaTypePrefix}${field}`];
    };

    const setMediaData = (field: string, value: any) => {
        const mediaTypePrefix = selectedMediaType.toLowerCase();
        updateTempSetting(`${mediaTypePrefix}${field}`, value);
    };

    // Validate media file based on selected type
    const validateMediaFile = (file: UploadedFile): Promise<boolean> => {
        return new Promise((resolve) => {
            const fileType = file.type.toLowerCase();

            if (selectedMediaType === 'Image') {
                // Allow only image files
                if (!fileType.startsWith('image/')) {
                    showToast("Error", "Please select an image file", "error");
                    resolve(false);
                    return;
                }

                // Size check for images (e.g., max 5MB)
                if (file.file.size > 5 * 1024 * 1024) {
                    showToast("Error", "Image size must be less than 5MB", "error");
                    resolve(false);
                    return;
                }

                // Validate image dimensions if needed
                const img = new Image();
                img.src = URL.createObjectURL(file.file);

                img.onload = () => {
                    URL.revokeObjectURL(img.src);
                    resolve(true);
                };

                img.onerror = () => {
                    URL.revokeObjectURL(img.src);
                    showToast("Error", "Invalid image file", "error");
                    resolve(false);
                };
            } else if (selectedMediaType === 'Video') {
                // Allow only video files
                if (!fileType.startsWith('video/')) {
                    showToast("Error", "Please select a video file", "error");
                    resolve(false);
                    return;
                }

                // Size check for videos (e.g., max 50MB)
                if (file.file.size > 50 * 1024 * 1024) {
                    showToast("Error", "Video size must be less than 50MB", "error");
                    resolve(false);
                    return;
                }

                resolve(true);
            } else {
                resolve(false);
            }
        });
    };

    const handleUpload = async (
        file: UploadedFile,
        onProgress?: (percent: number) => void
    ): Promise<{ key: string }> => {
        const parts = file.name?.split(".");
        const extension = parts.pop();

        try {
            const { data } = await mediaPreSignedUrl({
                fileName: file.name,
                fileType: extension,
            });

            if (!data?.url) {
                showToast("Error", "Could not generate upload URL", "error");
                return { key: '' };
            }

            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open(data.method, data.url, true);
                xhr.setRequestHeader("Content-Type", file.type);

                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable && onProgress) {
                        const percent = Math.round((event.loaded / event.total) * 100);
                        onProgress(percent);
                    }
                };

                xhr.onload = () => {
                    if (xhr.status === 200 || xhr.status === 204) {
                        resolve({ key: data.key });
                    } else {
                        reject(new Error("Upload failed"));
                    }
                };

                xhr.onerror = () => reject(new Error("Network error"));
                xhr.send(file.file);
            });
        } catch (err) {
            showToast("Error", "Upload preparation failed", "error");
            return { key: '' };
        }
    };

    return (
        <>
            <Container theme={theme}>
                {/* Section Items */}


                {/* Container Background Color */}
                <PropertySection theme={theme} >
                    <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor} className='mb-2'>
                        Container Background Color
                    </StyledText>
                    <Row style={{ alignItems: 'center', position: 'relative', gap: '10px', padding: '0px 10px' }}>
                        {/* Invisible but functional color input */}
                        <input
                            type="color"
                            value={tempSettings.bgColor || '#C9DFFF'}
                            onChange={(e) => updateTempSetting('bgColor', e.target.value)}
                            style={{
                                width: '105px',
                                height: '42px',
                                // remove all browser styles
                                appearance: 'none',
                                WebkitAppearance: 'none',
                                border: 'none',
                                outline: 'none',
                                boxShadow: 'none',

                                opacity: 0,
                                cursor: 'pointer',
                                position: 'absolute',
                                zIndex: 2,

                                padding: 0,
                                margin: 0,
                            }}
                        />

                        {/* Visible preview box */}
                        <div
                            style={{
                                width: '105px',
                                height: '42px',
                                border: `1px solid ${theme.CreateFormTheme.colors.inputBorders}`,
                                borderRadius: '8px',
                                backgroundColor: tempSettings.bgColor || '#C9DFFF',
                                position: 'relative',
                                zIndex: 1,
                            }}
                        />

                        <HexInput
                            type="text"
                            theme={theme}
                            value={tempSettings.bgColor || '#C9DFFF'}
                            onChange={(e) => updateTempSetting('bgColor', e.target.value)}
                        />
                    </Row>
                </PropertySection>

                {/* Background Image */}
                <PropertySection theme={theme}>
                    <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor} className='mb-2'>
                        Background Image
                    </StyledText>
                    <Formik
                        initialValues={{
                            file: [],
                            mediaPreview: getMediaData('Preview') || '',
                        }}
                        enableReinitialize
                        onSubmit={(values) => {
                        }}
                    >
                        {({ values, setFieldValue }) => {
                            const handleFileChange = async (files: UploadedFile[]) => {
                                if (!files.length) return;
                                const file = files[0];

                                // Validate based on media type
                                const isValid = await validateMediaFile(file);
                                if (!isValid) {
                                    setHasUploaded(false);
                                    setUploadProgress(0);
                                    setIsUploading(false);

                                    // Reset everything for current media type
                                    setFieldValue('file', []);
                                    setFieldValue('mediaPreview', '');
                                    setMediaData('Url', '');
                                    setMediaData('Preview', '');

                                    // Force reset
                                    setUploadKey(prev => prev + 1);
                                    return;
                                }

                                // Clear external URL when uploading a file
                                const externalUrlKey = `${selectedMediaType.toLowerCase()}ExternalURL`;
                                if (tempSettings[externalUrlKey]) {
                                    updateTempSetting(externalUrlKey, '');
                                }

                                // Create preview
                                const reader = new FileReader();
                                reader.onload = () => {
                                    if (reader.result) {
                                        setFieldValue('mediaPreview', reader.result);
                                        setMediaData('Preview', reader.result as string);
                                    }
                                };
                                reader.readAsDataURL(file.file);

                                // Start upload
                                setIsUploading(true);
                                setUploadProgress(0);

                                handleUpload(file, setUploadProgress)
                                    .then(({ key }) => {
                                        if (key) {
                                            setFieldValue('mediaUrl', key);
                                            setMediaData('Url', key);
                                            setMediaData('FileName', file.name);
                                            setMediaData('FileSize', file.file.size);
                                            setHasUploaded(true);
                                            showToast('Success', `${selectedMediaType} uploaded successfully`, 'success');
                                        }
                                    })
                                    .catch(() => {
                                        showToast('Error', `Failed to upload ${selectedMediaType.toLowerCase()}`, 'error');
                                    })
                                    .finally(() => {
                                        setIsUploading(false);
                                    });
                            };

                            const handleRemoveMedia = () => {
                                setFieldValue('mediaUrl', '');
                                setFieldValue('mediaPreview', '');
                                setMediaData('Url', '');
                                setMediaData('Preview', '');
                                setMediaData('FileName', '');
                                setMediaData('FileSize', undefined);
                                setHasUploaded(false);
                            };

                            const hasUploadedMedia = getMediaData('Url') || getMediaData('Preview') || getMediaData('FileName');

                            return (
                                <div style={{
                                    opacity: tempSettings[`${selectedMediaType.toLowerCase()}ExternalURL`] ? 0.5 : 1,
                                    pointerEvents: tempSettings[`${selectedMediaType.toLowerCase()}ExternalURL`] ? 'none' : 'auto',
                                    position: 'relative'
                                }}>
                                    <FormikImageUpload
                                        key={uploadKey}
                                        name='file'
                                        accept={selectedMediaType === 'Image' ? 'image/*' : 'video/*'}
                                        onChange={handleFileChange}
                                        setHasUploaded={setHasUploaded}
                                        previewUrl={values.mediaPreview}
                                        uploadProgress={uploadProgress}
                                        isUploading={isUploading}
                                        removeFile={handleRemoveMedia}
                                        existingFileName={getMediaData('FileName')}
                                        existingFileSize={getMediaData('FileSize')}
                                    />
                                </div>
                            );
                        }}
                    </Formik>
                </PropertySection>

                {/* Image Overlay */}
                <PropertySection theme={theme} >
                    <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor} className='mb-2'>
                        Image Overlay
                    </StyledText>
                    <Row style={{ alignItems: 'center', position: 'relative', gap: '10px', padding: '0px 10px' }}>
                        <input
                            type="color"
                            value={tempSettings.imageOverlayColor || '#000000'}
                            onChange={(e) => updateTempSetting('imageOverlayColor', e.target.value)}
                            style={{
                                width: '105px',
                                height: '42px',

                                // remove all browser styles
                                appearance: 'none',
                                WebkitAppearance: 'none',
                                border: 'none',
                                outline: 'none',
                                boxShadow: 'none',

                                opacity: 0,
                                cursor: 'pointer',
                                position: 'absolute',
                                zIndex: 2,

                                padding: 0,
                                margin: 0,
                            }}
                        />

                        <div
                            style={{
                                width: '105px',
                                height: '42px',
                                border: `1px solid ${theme.CreateFormTheme.colors.inputBorders}`,
                                borderRadius: '5px',
                                backgroundColor: tempSettings.imageOverlayColor || '#000000',
                                position: 'relative',
                                zIndex: 1,
                            }}
                        />

                        <div className='d-flex align-items-center' style={{ flex: 1, border: `1px solid ${theme.CreateFormTheme.colors.inputBorders}`, borderRadius: '5px', padding: '0px', background: theme.CorporateTheme.colors.background }}>
                            <HexInput
                                theme={theme}
                                style={{ border: 'none', outline: 'none', boxShadow: 'none' }}
                                value={tempSettings.imageOverlayColor || '#000000'}
                                onChange={(e) => updateTempSetting('imageOverlayColor', e.target.value)}
                            />
                            <input
                                style={{ padding: '0px 9px', fontSize: '14px', fontFamily: font.lato, fontWeight: 600, height: 42, border: 'none', outline: 'none', boxShadow: 'none', background: theme.CorporateTheme.colors.headerBG, borderTopRightRadius: '5px', borderBottomRightRadius: '5px', borderLeft: `1px solid ${theme.CreateFormTheme.colors.inputBorders}`, width: 55 }}
                                type='text'
                                value={tempSettings.imageOverlayOpacity || '100%'}
                                onChange={(e) => updateTempSetting('imageOverlayOpacity', e.target.value)}
                            />
                        </div>
                    </Row>
                </PropertySection>



            </Container>
            {currentSection && getSectionElements().map((columnData, idx) => (
                <ContentArrangmentSection theme={theme} key={idx}>
                    <StyledText
                        fontSize='13px'
                        fontWeight={600}
                        textColor={theme.CorporateTheme.colors.primary}
                        className='mb-3'
                    >
                        {isSingleColumn
                            ? 'Content Arrangement'
                            : columnData.column === 'left' ? 'Left Column Items' : 'Right Column Items'}
                    </StyledText>

                    {columnData.elements.length === 0 ? (
                        <EmptyState theme={theme}>
                            <StyledText
                                fontSize='13px'
                                fontWeight={400}
                                textColor={theme.CorporateTheme.colors.textSecondary}
                            >
                                No items in this {isSingleColumn ? 'section' : 'column'} yet
                            </StyledText>
                        </EmptyState>
                    ) : (
                        <ItemsList>
                            {columnData.elements.map((element, index) => (
                                <ItemCard
                                    key={element.id}
                                    theme={theme}
                                    draggable
                                    $isDragging={draggedItemId === element.id}
                                    $isDragOver={dragOverItemId === element.id}
                                    onDragStart={() => handleDragStart(element.id, columnData.column)}
                                    onDragOver={(e) => handleDragOver(e, element.id)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, element.id, columnData.column)}
                                    onDragEnd={handleDragEnd}
                                >
                                    <DraggableIcon />
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1, gap: 5 }}>
                                        <ItemContent theme={theme} $isDragging={draggedItemId === element.id} $isDragOver={dragOverItemId === element.id}>
                                            <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>
                                                {element.type.replace('-', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                            </StyledText>
                                            {/* {element.content && (
                                            <ItemPreview theme={theme}>
                                                {element.content.substring(0, 40)}
                                            </ItemPreview>
                                        )} */}
                                        </ItemContent>
                                        <DeleteButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteElement(element.id, columnData.column);
                                            }}
                                            theme={theme}
                                            title="Delete element"
                                        >
                                            <DeleteIcon color={theme.CorporateTheme.colors.headerColor} />
                                        </DeleteButton>
                                    </div>
                                </ItemCard>
                            ))}
                        </ItemsList>
                    )}
                </ContentArrangmentSection>
            ))}
        </>
    )
}

const Container = styled.div<{ theme: Theme }>`
    display: flex;
    flex-direction: column; 
    border-radius: 8px 8px 0 0;
    padding: 16px;
    background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    `

const PropertySection = styled.div<{ theme: Theme }>`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 26px;
`;

const ContentArrangmentSection = styled.div<{ theme: Theme }>`
    display: flex;
    flex-direction: column;
    gap: 8px;
    background-color: ${({ theme }) => theme.CorporateTheme.colors.headerBG};
    padding: 30px 20px;
`;


const OptionsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
`;

const OptionButton = styled.button<{ theme: Theme, $isSelected?: boolean }>`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap:10px;
    padding: 8px 12px;
    border: 1px solid ${({ theme, $isSelected }) =>
        $isSelected ? theme.CorporateTheme.colors.accentOrange : theme.CorporateTheme.colors.border};
    background-color: ${({ theme, $isSelected }) =>
        $isSelected ? hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1) : theme.CorporateTheme.colors.background};
    color: ${({ theme, $isSelected }) =>
        $isSelected ? theme.CorporateTheme.colors.accentOrange : theme.CorporateTheme.colors.textPrimary};
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: center;
    height:72px;
    
    /* Remove margin */
    margin: 0;
    
    /* Default no border-radius */
    border-radius: 0;
    
    /* First button - rounded left corners */
    &:first-child {
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
    }

    /* Last button - rounded right corners */
    &:last-child {
        border-top-right-radius: 8px;
        border-bottom-right-radius: 8px;
    }

    /* Remove double borders between buttons */
    &:not(:first-child) {
        margin-left: -1px;
    }

    /* IMPORTANT: Set z-index for all buttons to prevent overlap issues */
    position: relative;
    z-index: ${({ $isSelected }) => $isSelected ? 2 : 1};

    &:hover {
        background-color: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1)};
        border-color: ${({ theme }) => theme.CorporateTheme.colors.accentOrange};
        color: ${({ theme }) => theme.CorporateTheme.colors.accentOrange};
        z-index: 3; /* Higher z-index on hover */
    }

    /* Selected state styling */
    ${({ $isSelected, theme }) => $isSelected && `
        background-color: ${hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1)};
        border-color: ${theme.CorporateTheme.colors.accentOrange};
        color: ${theme.CorporateTheme.colors.accentOrange};
        font-weight: 600;
        z-index: 2;
    `}
`;

const HexInput = styled(Form.Control) <{ theme: Theme }>`
    flex: 1;
  height: 42px;
  background: transparent;
  color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
  border: 1px solid ${({ theme }) => theme.CreateFormTheme.colors.inputBorders};
`;

const TextInput = styled.input<{ theme: Theme }>`
    padding: 10px 12px;
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
    color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
    font-size: 14px;
    
    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.CorporateTheme.colors.primary};
    }
    
    &::placeholder {
        color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
    }
`;

const Select = styled.select<{ theme: Theme }>`
    padding: 10px 12px;
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    background-color: ${({ theme }) => theme.CorporateTheme.colors.headerBG};
    color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
    font-size: 14px;
    
    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.CorporateTheme.colors.primary};
    }
    
    option {
        background-color: ${({ theme }) => theme.CorporateTheme.colors.headerBG};
        color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
    }
`;


const AlignmentCard = styled.button<{
    theme: any;
    $isSelected: boolean;
}>`
  display: flex;
  align-items: center;
    border:none;
  cursor: pointer;
    gap: 6px;
  background: #fff;

  transition: all 0.2s ease;
`;

const ItemsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const ItemCard = styled.div<{ theme: Theme; $isDragging?: boolean; $isDragOver?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    // padding: 12px;
    border-radius: 8px;
 
    transition: all 0.2s ease;
    cursor: grab;
    opacity: ${({ $isDragging }) => $isDragging ? 0.5 : 1};

`;

const DragHandle = styled.div<{ theme: Theme }>`
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 4px;
    cursor: grab;
    opacity: 0.4;
    transition: opacity 0.2s ease;

    ${ItemCard}:hover & {
        opacity: 1;
    }
`;

const DragDots = styled.div`
    display: flex;
    gap: 3px;

    &::before,
    &::after {
        content: '';
        width: 3px;
        height: 3px;
        border-radius: 50%;
        background-color: currentColor;
    }
`;

const ItemContent = styled.div <{ theme: Theme; $isDragging?: boolean; $isDragOver?: boolean }> `
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 8px 10px;
    border-radius: 4px;
    gap: 4px;
    flex: 1;
    min-width: 0;
    height:40px;
       border: 1px solid ${({ theme, $isDragOver }) =>
        $isDragOver ? theme.CorporateTheme.colors.primary : theme.CreateFormTheme.colors.inputBorders};
    background-color: ${({ theme, $isDragOver }) =>
        $isDragOver ? hexToRgba(theme.CorporateTheme.colors.primary, 0.05) : theme.CorporateTheme.colors.background};

    
    &:hover {
        border-color: ${({ theme }) => theme.CorporateTheme.colors.primary};
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    &:active {
        cursor: grabbing;
    }
`;

const ItemType = styled.div<{ theme: Theme }>`
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => theme.CorporateTheme.colors.headerColor};
    font-family: ${font.lato};
`;

const ItemPreview = styled.div<{ theme: Theme }>`
    font-size: 12px;
    font-weight: 400;
    color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
    font-family: ${font.lato};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const EmptyState = styled.div<{ theme: Theme }>`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    border-radius: 8px;
    border: 1px dashed ${({ theme }) => theme.CorporateTheme.colors.border};
    background-color: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.border, 0.05)};
`;

const DeleteButton = styled.button<{ theme: Theme }>`
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 11px;
    border-radius: 4px;
    transition: all 0.2s ease;
    background-color: ${({ theme }) => theme.CorporateTheme.colors.background};

    &:hover {
        opacity: 1;
        background-color: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1)};
    }

    svg {
        width: 18px;
        height: 18px;
    }
`;

export default SectionProperties
